import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import Button from '../../../../components/Button/Button';
import Counter from '../../../../components/Counter/Counter';
import DatePickerField from '../../../../components/DatePickerField/DatePickerField';
import { InfoIcon } from '../../../../components/Icon/Icon';
import SelectDropdown from '../../../../components/SelectDropdown/SelectDropdown';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { useLocale } from '../../../../context/LocaleContext';
import { SectionTitleStyled, TitleWithTooltipWrapperStyled } from '../../HomePage.styled';
import useTransportationAndHotelOffset from './hooks/useTransportationAndHotelOffset';
import {
  ButtonsWrapperStyled,
  FieldLabelStyled,
  FieldsColStyled,
  FieldsRowStyled,
  FormWrapperStyled,
  getSelectStyles,
  SubTitleStyled,
} from './TransportationAndHotelOffset.styled';

type TransportationAndHotelOffsetPropsT = {
  customerId: string;
  customerName: string;
};

const TransportationAndHotelOffset: React.FC<TransportationAndHotelOffsetPropsT> = ({
  customerId,
  customerName,
}) => {
  const { selectedLocale } = useLocale();
  const theme = useTheme();
  const { rooms, formValues, onFormFieldChange, onClickHotel, onClickHotelAndTransport } =
    useTransportationAndHotelOffset(customerId, customerName);
  const oneYearFromNow = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

  return (
    <>
      <div>
        <TitleWithTooltipWrapperStyled>
          <SectionTitleStyled level={3} align="center">
            <FormattedMessage
              id="footprint.calculator.title"
              defaultMessage="Carbon Footprint Calculator"
            />
          </SectionTitleStyled>

          <Tooltip element={<InfoIcon />}>
            <FormattedMessage
              id="footprint.calculatorTitle.tooltip.text"
              defaultMessage="This feature allows you to calculate the footprint of the room you stay in and gives you an option to calculate the carbon produced from your flight, ground, and sea transport."
            />
          </Tooltip>
        </TitleWithTooltipWrapperStyled>

        <SubTitleStyled level={3} align="center">
          <FormattedMessage
            id="footprint.calculator.subTitle"
            defaultMessage="Calculate and offset the CO2 you generate during your stay at our hotel"
          />
        </SubTitleStyled>

        <FormWrapperStyled>
          <FieldsRowStyled>
            <FieldsColStyled>
              <FieldLabelStyled>
                <FormattedMessage id="form.roomType" defaultMessage="Room type" />
              </FieldLabelStyled>

              <SelectDropdown
                options={rooms}
                value={formValues.roomType}
                customStyles={getSelectStyles(theme)}
                onChange={selectedRoom => {
                  onFormFieldChange('roomType', selectedRoom);
                }}
              />
            </FieldsColStyled>

            <FieldsColStyled>
              <FieldLabelStyled>
                <FormattedMessage id="form.hotel" defaultMessage="Hotel" />
              </FieldLabelStyled>

              <SelectDropdown isDisabled value={formValues.hotel} />
            </FieldsColStyled>
          </FieldsRowStyled>

          <FieldsRowStyled>
            <FieldsColStyled>
              <FieldLabelStyled>
                <FormattedMessage id="form.numberOfRooms" defaultMessage="Number of rooms" />
              </FieldLabelStyled>
              <Counter
                max={100}
                value={formValues.numberOfRooms}
                onChange={val => {
                  onFormFieldChange('numberOfRooms', val);
                }}
              />
            </FieldsColStyled>

            <FieldsColStyled>
              <FieldLabelStyled>
                <FormattedMessage
                  id="form.numberOfGuests"
                  defaultMessage="Total number of guests"
                />
              </FieldLabelStyled>
              <Counter
                max={300}
                value={formValues.totalNumberOfGuests}
                onChange={val => {
                  onFormFieldChange('totalNumberOfGuests', val);
                }}
              />
            </FieldsColStyled>
          </FieldsRowStyled>

          <FieldsRowStyled>
            <FieldsColStyled>
              <FieldLabelStyled>
                <FormattedMessage id="form.arrivalDate" defaultMessage="Arrival date" />
              </FieldLabelStyled>
              <DatePickerField
                locale={selectedLocale}
                maxDate={oneYearFromNow}
                value={formValues.arrivalDate}
                onChange={(date: Date) => {
                  onFormFieldChange('arrivalDate', date);
                }}
              />
            </FieldsColStyled>

            <FieldsColStyled>
              <FieldLabelStyled>
                <FormattedMessage id="form.numberOfNights" defaultMessage="Number of nights" />
              </FieldLabelStyled>
              <Counter
                max={50}
                value={formValues.numberOfNights}
                onChange={val => {
                  onFormFieldChange('numberOfNights', val);
                }}
              />
            </FieldsColStyled>
          </FieldsRowStyled>
        </FormWrapperStyled>
      </div>

      <ButtonsWrapperStyled>
        <Button onClick={onClickHotel}>
          <FormattedMessage id="button.hotelStayOnly" defaultMessage="Hotel stay only" />
        </Button>
        <Button isInverted onClick={onClickHotelAndTransport}>
          <FormattedMessage
            id="button.hotelAndTransportation"
            defaultMessage="Hotel stay + Transportation"
          />
        </Button>
      </ButtonsWrapperStyled>
    </>
  );
};

export default TransportationAndHotelOffset;
